<template>
  <div class="tbank-qr">
    <!-- Idle: blurred placeholder + button -->
    <div v-if="phase === 'idle'" class="tbank-qr__idle">
      <div class="tbank-qr__blur-wrap">
        <canvas ref="qrPlaceholder" class="tbank-qr__canvas tbank-qr__canvas--blurred" />
      </div>
      <q-btn
        v-if="!triggerOnly"
        unelevated
        no-caps
        color="primary"
        icon="qr_code_2"
        label="Показать QR-код"
        class="q-mt-sm"
        @click="start"
      />
    </div>

    <!-- Loading -->
    <div v-else-if="phase === 'loading'" class="tbank-qr__center">
      <q-spinner color="primary" size="48px" />
      <div class="q-mt-sm text-caption">Формируем QR-код…</div>
    </div>

    <!-- Error -->
    <div v-else-if="phase === 'error'" class="tbank-qr__center">
      <q-icon name="error_outline" color="negative" size="40px" />
      <div class="q-mt-sm text-caption text-negative">{{ errorText }}</div>
      <q-btn
        flat
        dense
        no-caps
        color="primary"
        label="Попробовать снова"
        class="q-mt-sm"
        @click="reset"
      />
    </div>

    <!-- QR shown, waiting for payment -->
    <div v-else-if="phase === 'qr'">
      <canvas ref="qrCanvas" class="tbank-qr__canvas" />
      <div class="text-caption q-mt-xs text-center">
        Откройте приложение банка и отсканируйте код
      </div>
    </div>

    <!-- Paid -->
    <div v-else-if="phase === 'confirmed'" class="tbank-qr__center">
      <q-icon name="check_circle" color="positive" size="48px" />
      <div class="q-mt-sm text-subtitle2 text-positive">Оплата прошла успешно</div>
    </div>

    <!-- Rejected -->
    <div v-else-if="phase === 'rejected'" class="tbank-qr__center">
      <q-icon name="cancel" color="negative" size="48px" />
      <div class="q-mt-sm text-caption text-negative">Платёж отклонён</div>
      <q-btn
        flat
        dense
        no-caps
        color="primary"
        label="Попробовать снова"
        class="q-mt-sm"
        @click="reset"
      />
    </div>
  </div>
</template>

<script>
import QRCode from "qrcode";

const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATUSES = ["CONFIRMED", "REJECTED", "CANCELED", "DEADLINE_EXPIRED"];

export default {
  name: "TBank",

  props: {
    amount:      { type: Number,  required: true },
    email:       { type: String,  default: "" },
    payerName:   { type: String,  default: "" },
    triggerOnly: { type: Boolean, default: false },
  },

  data() {
    return {
      phase: "idle",
      errorText: "",
      paymentId: null,
      pollTimer: null,
    };
  },

  mounted() {
    this.renderPlaceholder();
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    async start() {
      this.phase = "loading";
      this.errorText = "";
      this.stopPolling();

      try {
        // 1. Init
        const initBody = { Amount: this.amount, Receipt: this.buildReceipt() };
        if (this.payerName) initBody.PayerName = this.payerName;
        const initRes = await this.$api.post("/api/tbank/init/", initBody);
        if (!initRes.data.Success) {
          return this.setError(initRes.data.Message || "Ошибка инициализации платежа");
        }
        this.paymentId = String(initRes.data.PaymentId);
        this.$emit("inited", { paymentUrl: initRes.data.PaymentURL });

        // 2. GetQr
        const qrRes = await this.$api.post("/api/tbank/getqr/", {
          PaymentId: this.paymentId,
          DataType: "PAYLOAD",
        });
        if (!qrRes.data.Success) {
          return this.setError(qrRes.data.Message || "Ошибка получения QR-кода");
        }
        const qrData = qrRes.data.Data;

        // 3. Render QR onto canvas
        this.phase = "qr";
        await this.$nextTick();
        await QRCode.toCanvas(this.$refs.qrCanvas, qrData, {
          width: 220,
          margin: 1,
          color: { dark: "#000000", light: "#ffffff" },
        });

        // 4. Start polling
        this.startPolling();
      } catch (e) {
        this.setError("Ошибка соединения. Попробуйте позже.");
      }
    },

    startPolling() {
      this.pollTimer = setInterval(this.checkState, POLL_INTERVAL_MS);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    async checkState() {
      try {
        const res = await this.$api.post("/api/tbank/getstate/", {
          PaymentId: this.paymentId,
        });
        const status = res.data.Status;
        if (!TERMINAL_STATUSES.includes(status)) return;

        this.stopPolling();
        if (status === "CONFIRMED") {
          this.phase = "confirmed";
          this.$emit("paid", this.paymentId);
        } else {
          this.phase = "rejected";
          this.$emit("rejected", status);
        }
      } catch (_) {
        // transient network error — keep polling
      }
    },

    buildReceipt() {
      const receipt = {
        Taxation: "usn_income",
        Items: [
          {
            Name: "Доступ к сервису ПОЧТУМ.РФ (1 год)",
            Price: this.amount,
            Quantity: 1,
            Amount: this.amount,
            Tax: "none",
            PaymentMethod: "full_payment",
            PaymentObject: "service",
            MeasurementUnit: "шт",
          },
        ],
      };
      if (this.email) receipt.Email = this.email;
      return receipt;
    },

    async reset() {
      this.phase = "idle";
      this.errorText = "";
      this.paymentId = null;
      await this.$nextTick();
      this.renderPlaceholder();
    },

    async renderPlaceholder() {
      await this.$nextTick();
      if (this.$refs.qrPlaceholder) {
        await QRCode.toCanvas(this.$refs.qrPlaceholder, "ПОЧТУМ", {
          width: 220,
          margin: 1,
          color: { dark: "#000000", light: "#ffffff" },
        });
      }
    },

    setError(text) {
      this.phase = "error";
      this.errorText = text;
    },
  },
};
</script>

<style lang="sass" scoped>
.tbank-qr
  text-align: center

.tbank-qr__center
  display: flex
  flex-direction: column
  align-items: center
  padding: 8px 0

.tbank-qr__canvas
  display: block
  margin: 0 auto
  border-radius: 8px

.tbank-qr__canvas--blurred
  filter: blur(6px)
  pointer-events: none

.tbank-qr__idle
  display: flex
  flex-direction: column
  align-items: center

.tbank-qr__blur-wrap
  overflow: hidden
  border-radius: 8px
</style>
